import * as THREE from 'three';

/**
 * Minimal full-screen pass plumbing — the EffectComposer we actually want.
 *
 * One shared full-screen *triangle* (not a quad: no diagonal seam, one fewer
 * vertex, and the GPU rasterises it in a single primitive) and one shared
 * camera. Nothing here allocates after construction.
 */

const TRI = new THREE.BufferGeometry();
TRI.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
TRI.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 2, 0, 0, 2]), 2));
TRI.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 8);

export const FULLSCREEN_VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }`;

export class Pass {
  constructor(fragmentShader, uniforms = {}, opts = {}) {
    this.uniforms = uniforms;
    this.material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: opts.vertexShader || FULLSCREEN_VERT,
      fragmentShader,
      defines: opts.defines || {},
      depthTest: false,
      depthWrite: false,
      blending: opts.blending ?? THREE.NoBlending,
      transparent: !!opts.transparent,
      toneMapped: false,
    });
    this.mesh = new THREE.Mesh(TRI, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
  }
  set(name, value) { const u = this.uniforms[name]; if (u) u.value = value; return this; }
  dispose() { this.material.dispose(); }
}

export class Composer {
  constructor(renderer) {
    this.renderer = renderer;
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.camera.matrixAutoUpdate = false;
    this.camera.updateMatrixWorld();
  }
  /** Draw `pass` into `target` (null = the canvas). No clear: the tri covers all. */
  run(pass, target) {
    const r = this.renderer;
    r.setRenderTarget(target || null);
    r.render(pass.mesh, this.camera);
  }
}

/** HDR colour target; `depth` attaches a sampleable depth texture. */
export function makeRT(w, h, opts = {}) {
  const rt = new THREE.WebGLRenderTarget(Math.max(1, w | 0), Math.max(1, h | 0), {
    type: opts.type ?? THREE.HalfFloatType,
    format: opts.format ?? THREE.RGBAFormat,
    minFilter: opts.minFilter ?? THREE.LinearFilter,
    magFilter: opts.magFilter ?? THREE.LinearFilter,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    depthBuffer: !!opts.depth,
    stencilBuffer: false,
    generateMipmaps: false,
    samples: 0,
  });
  rt.texture.colorSpace = THREE.NoColorSpace;
  rt.texture.name = opts.name || 'rt';
  if (opts.depth) {
    const dt = new THREE.DepthTexture(rt.width, rt.height, THREE.UnsignedIntType);
    dt.format = THREE.DepthFormat;
    dt.minFilter = THREE.NearestFilter;
    dt.magFilter = THREE.NearestFilter;
    rt.depthTexture = dt;
  }
  return rt;
}

export function resizeRT(rt, w, h) {
  w = Math.max(1, w | 0); h = Math.max(1, h | 0);
  if (rt.width === w && rt.height === h) return false;
  rt.setSize(w, h);
  return true;
}
